/* eslint-disable max-lines */
import { IS_HIDE_PRO_MONTHLY_OPTION } from '@/app-shell/flags/commerce-flags';
import { useMutateCheckoutV2 } from '@/hooks/mutation/useMutateCheckoutV2';
import { useMutateExtendReverseTrial } from '@/hooks/mutation/useMutateExtendReverseTrial';
import {
  formatPriceWithCurrency,
  getCurrencySymbol,
  getDisplayedPriceFromPlan,
  getPlanFromPeriod,
  getTierProduct,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
// migration-allow(family-importers): trial revert refreshes the current user owned by its query family
import { invalidateCurrentUser } from '@/hooks/query/useQueryUser';
import {
  invalidateCurrentUserBillingInfo,
  useQueryUserBillingInfo,
} from '@/hooks/query/useQueryUserBillingInfo';
import { cn } from '@/lib/cn';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { getStripeCallbackRedirect, getStripeReturnRedirect } from '@/utils/scribeHTTP';
import { PeriodTabsList } from '@heidi/commerce';
import { Period, TRIAL_EXTENSION_HOURS } from '@heidi/commerce/contracts';
import {
  endTrialModalClosed,
  endTrialModalDowngradeClicked,
  endTrialModalOpenAtom,
  endTrialModalOpened,
  endTrialModalPeriodChanged,
  endTrialModalUpgradeClicked,
  extendTrialModalOpenAtom,
  extendTrialModalOpened,
  useMutateRevertReverseTrial as useMutateRevertReverseTrialCore,
} from '@heidi/commerce/data';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TierProFilled } from '@heidi/design-system/icons';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerHeader,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { SegmentedControl } from '@heidi/design-system/segmented-control';
import { toast } from '@heidi/design-system/toast';
import {
  TypographyCaption,
  TypographyH2,
  TypographyH3,
  TypographyP,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { logger } from '@heidi/runtime/logger';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useAtom, useSetAtom } from 'jotai';
import {
  ArrowRight,
  Blocks,
  Cuboid,
  Rocket,
  Snail,
  Sparkle,
  Sparkles,
  User,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

type TrialType = 'pro' | 'practice';

function useMutateRevertReverseTrial(trialType: TrialType = 'pro') {
  const { t } = useTranslations();
  const description =
    trialType === 'practice'
      ? t('revertTrialToast.descriptionPractice')
      : t('revertTrialToast.descriptionPro');

  return useMutateRevertReverseTrialCore({
    onRevertSuccess: () => {
      void invalidateCurrentUser();
      void invalidateCurrentUserBillingInfo();
      toast({
        title: t('revertTrialToast.title'),
        description,
      });
    },
    onRevertError: (error) => {
      logger.error('Error reverting trial:', error);
      toast({
        tone: 'destructive',
        title: t('revertTrialToast.errorTitle'),
        description: t('revertTrialToast.errorDescription'),
      });
    },
  });
}

export const EndTrialModal = () => {
  const { data: billingInfo } = useQueryUserBillingInfo((data) => ({
    is_in_reverse_free_trial: data?.is_in_reverse_free_trial,
    is_in_free_trial: data?.is_in_free_trial,
    trial_end_date: data?.trial_end_date,
    is_reverse_trial_extendable: data?.is_reverse_trial_extendable,
    tier: data?.tier,
  }));

  // This modal is only for PRO trial users, not PRO_PLUS (Practice) trial users
  const isProTrialUser = billingInfo?.tier === SubscriptionTier.PRO;

  const [isAtomOpen, setIsOpen] = useAtom(endTrialModalOpenAtom);
  const isOpen = !isAppleCompliantBuild() && isAtomOpen;
  const setExtendTrialModalOpen = useSetAtom(extendTrialModalOpenAtom);

  const [period, setPeriod] = useState<Period>(Period.ONE_YEAR);
  const [isDismissing, setIsDismissing] = useState(false);
  const { data: products } = useQueryProducts();
  const product = getTierProduct(SubscriptionTier.PRO, products);
  const { t, locale } = useTranslations();

  const plan = getPlanFromPeriod(period, product);
  const currencyCode = plan?.currency ?? '';
  const currencySymbol = getCurrencySymbol(currencyCode);
  const freePrice = formatPriceWithCurrency('0', currencyCode, currencySymbol);

  const { mutate: createCheckoutSession, isPending: isCreatingCheckout } =
    useMutateCheckoutV2();
  const { mutate: extendFreeTrial } = useMutateExtendReverseTrial();
  const { mutate: revertTrial, isPending: isRevertTrialPending } =
    useMutateRevertReverseTrial();

  const DEFAULT_END_TRIAL_EXTENSION_HOURS = Number(
    TRIAL_EXTENSION_HOURS.FORTY_EIGHT_HOURS
  );

  const onModalOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsOpen(isOpen);
      if (isOpen) {
        endTrialModalOpened();
      } else {
        endTrialModalClosed();
      }
    },
    [setIsOpen]
  );

  useEffect(() => {
    // Only show this modal for PRO trial users, not PRO_PLUS (Practice) users
    if (
      !billingInfo?.trial_end_date ||
      !billingInfo.is_in_reverse_free_trial ||
      !isProTrialUser
    )
      return;

    const endDate = new Date(billingInfo?.trial_end_date);
    const now = new Date();

    if (endDate <= now) {
      onModalOpenChange(true);
      return;
    }

    const timeUntilExpiration = endDate.getTime() - now.getTime();
    const timerId = setTimeout(() => {
      onModalOpenChange(true);
    }, timeUntilExpiration);

    return () => clearTimeout(timerId);
  }, [
    billingInfo?.is_in_reverse_free_trial,
    billingInfo?.trial_end_date,
    isProTrialUser,
    onModalOpenChange,
  ]);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
    endTrialModalClosed();
  }, [setIsOpen]);

  const onExtendTrial = useCallback(() => {
    handleCloseModal();
    setExtendTrialModalOpen(true);
    extendTrialModalOpened();
  }, [handleCloseModal, setExtendTrialModalOpen]);

  const periodDisplay = useCallback(
    (p: Period) => {
      return p === Period.ONE_YEAR
        ? t('endTrialModal.proPlan.billedYearly')
        : t('endTrialModal.proPlan.billedMonthly');
    },
    [t]
  );

  const onSettled = useCallback(() => {
    setIsDismissing(false);
    handleCloseModal();
  }, [handleCloseModal]);

  const handleDismiss = useCallback(() => {
    if (isDismissing) return;
    setIsDismissing(true);

    if (billingInfo?.is_in_free_trial) {
      handleCloseModal();
      return;
    }

    if (billingInfo?.is_reverse_trial_extendable) {
      extendFreeTrial(DEFAULT_END_TRIAL_EXTENSION_HOURS, { onSettled });
    } else {
      revertTrial(undefined, { onSettled });
    }
  }, [
    isDismissing,
    billingInfo?.is_in_free_trial,
    billingInfo?.is_reverse_trial_extendable,
    handleCloseModal,
    extendFreeTrial,
    DEFAULT_END_TRIAL_EXTENSION_HOURS,
    onSettled,
    revertTrial,
  ]);

  const onStayPro = useCallback(() => {
    endTrialModalUpgradeClicked({
      period: period,
    });
    createCheckoutSession({
      plan_period: period,
      tier: SubscriptionTier.PRO,
      fe_endpoint: getStripeCallbackRedirect(billingInfo?.is_in_reverse_free_trial),
      fe_return_endpoint: getStripeReturnRedirect(),
    });
    handleCloseModal();
  }, [
    billingInfo?.is_in_reverse_free_trial,
    createCheckoutSession,
    handleCloseModal,
    period,
  ]);

  const handleDowngrade = useCallback(() => {
    endTrialModalDowngradeClicked();
    revertTrial(undefined, {
      onSettled: () => {
        handleCloseModal();
      },
    });
  }, [handleCloseModal, revertTrial]);

  if (!isOpen) {
    return null;
  }
  return (
    <DialogDrawer
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isDismissing) {
          handleDismiss();
        }
      }}
    >
      <DialogDrawerContent
        className={cn(
          'flex flex-col gap-8 p-3 sm:p-8',
          'h-fit',
          'max-h-fit',
          'w-full sm:max-w-[700px]',
          'mb-auto'
        )}
        hideCloseIcon
      >
        <DialogDrawerHeader className="flex flex-col items-center gap-4 text-center">
          <FramedIcon state="pro" />
          <span className="flex flex-col items-center gap-2 text-center">
            <TypographyH2>
              <Trans
                i18nKey="endTrialModal.title"
                defaults="Keep up your productivity <span />with Heidi Pro"
                components={{
                  span: <span className="hidden sm:inline" />,
                }}
              />
            </TypographyH2>
            {/* 14px on mobile, 16px on sm+ is intentional for this modal's compact mobile layout */}
            <TypographyP2 className="text-text-secondary sm:text-base">
              {t('endTrialModal.description')}
            </TypographyP2>
          </span>
        </DialogDrawerHeader>

        <SegmentedControl
          value={period}
          onValueChange={(value: string) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            if (value === Period.ONE_YEAR || value === Period.ONE_MONTH) {
              setPeriod(value);
              endTrialModalPeriodChanged({
                period: value,
              });
            }
          }}
          className="w-full gap-4"
        >
          {!IS_HIDE_PRO_MONTHLY_OPTION() && <PeriodTabsList />}

          <section className="flex snap-x snap-mandatory gap-4 overflow-x-auto p-px sm:grid sm:grid-cols-2">
            <div className="flex w-fit shrink-0 snap-center flex-col gap-4 rounded-md border border-violet-200 bg-violet-50 p-5 sm:w-auto">
              <div>
                <TypographyP2Bold className="text-tier-pro-text">
                  {t('endTrialModal.proPlan.title')}
                </TypographyP2Bold>
                <div className="flex items-center gap-2">
                  <TypographyH3>
                    {getDisplayedPriceFromPlan(
                      getPlanFromPeriod(period, product),
                      'month',
                      false,
                      locale
                    )}
                  </TypographyH3>
                  <div className="flex flex-col text-text-secondary">
                    <TypographyCaption>
                      {t('endTrialModal.proPlan.priceSuffix')}
                    </TypographyCaption>
                    <TypographyCaption>
                      {t('endTrialModal.proPlan.billedYearly') === periodDisplay(period)
                        ? t('endTrialModal.proPlan.billedYearly')
                        : t('endTrialModal.proPlan.billedMonthly')}
                      {period === Period.ONE_YEAR && (
                        <>
                          {t('endTrialModal.proPlan.priceSeparator')}{' '}
                          {`${getDisplayedPriceFromPlan(
                            getPlanFromPeriod(period, product),
                            'year',
                            false,
                            locale
                          )}`}
                        </>
                      )}
                    </TypographyCaption>
                  </div>
                </div>
              </div>

              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <Blocks className="text-tier-pro-text" width={18} height={18} />
                  <span>
                    <Trans
                      i18nKey="endTrialModal.proPlan.features.unlimitedTemplates"
                      defaults="<bold>Unlimited</bold> <text>custom templates</text>"
                      components={{
                        bold: (
                          <TypographyP2Bold className="inline">{''}</TypographyP2Bold>
                        ),
                        text: <TypographyP2 className="inline">{''}</TypographyP2>,
                      }}
                    />
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <Sparkles className="text-tier-pro-text" width={18} height={18} />
                  <span>
                    <Trans
                      i18nKey="endTrialModal.proPlan.features.unlimitedPrompts"
                      defaults="<bold>Unlimited</bold> <text>Ask Heidi prompts</text>"
                      components={{
                        bold: (
                          <TypographyP2Bold className="inline">{''}</TypographyP2Bold>
                        ),
                        text: <TypographyP2 className="inline">{''}</TypographyP2>,
                      }}
                    />
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <Rocket className="text-tier-pro-text" width={18} height={18} />
                  <span>
                    <Trans
                      i18nKey="endTrialModal.proPlan.features.prioritySpeed"
                      defaults="<bold>Priority</bold> <text>processing speed</text>"
                      components={{
                        bold: (
                          <TypographyP2Bold className="inline">{''}</TypographyP2Bold>
                        ),
                        text: <TypographyP2 className="inline">{''}</TypographyP2>,
                      }}
                    />
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <Rocket className="text-tier-pro-text" width={18} height={18} />
                  <TypographyP2Bold>
                    {t('endTrialModal.proPlan.features.teamFeatures')}
                  </TypographyP2Bold>
                </li>
              </ul>

              <div className="mt-auto">
                {!isAppleCompliantBuild() && (
                  <Button
                    size="lg"
                    variant="pro"
                    className="w-full"
                    disabled={isCreatingCheckout}
                    onClick={onStayPro}
                  >
                    <TierProFilled size={16} />
                    <TypographyP>
                      {isCreatingCheckout
                        ? t('endTrialModal.proPlan.button.loading')
                        : t('endTrialModal.proPlan.button.default')}
                    </TypographyP>
                  </Button>
                )}
              </div>
            </div>

            {/* Free Plan */}
            <div className="flex w-fit shrink-0 snap-center flex-col gap-4 rounded-md border border-border bg-surface-page p-5 sm:w-auto">
              <div>
                <TypographyP2Bold className="text-text-secondary">
                  {t('endTrialModal.freePlan.title')}
                </TypographyP2Bold>
                <TypographyH3>{freePrice}</TypographyH3>
              </div>

              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <Cuboid className="text-text-secondary" width={18} height={18} />
                  <span>
                    <Trans
                      i18nKey="endTrialModal.freePlan.features.limitedTemplates"
                      defaults="<bold>10x</bold> <text>custom templates a month</text>"
                      values={{ count: 10 }}
                      components={{
                        bold: (
                          <TypographyP2Bold className="inline">{''}</TypographyP2Bold>
                        ),
                        text: <TypographyP2 className="inline">{''}</TypographyP2>,
                      }}
                    />
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <Sparkle className="text-text-secondary" width={18} height={18} />
                  <span>
                    <Trans
                      i18nKey="endTrialModal.freePlan.features.limitedPrompts"
                      defaults="<bold>10x</bold> <text>Ask Heidi prompts a month</text>"
                      values={{ count: 10 }}
                      components={{
                        bold: (
                          <TypographyP2Bold className="inline">{''}</TypographyP2Bold>
                        ),
                        text: <TypographyP2 className="inline">{''}</TypographyP2>,
                      }}
                    />
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <Snail className="text-text-secondary" width={18} height={18} />
                  <span>
                    <Trans
                      i18nKey="endTrialModal.freePlan.features.basicSpeed"
                      defaults="<bold>Basic</bold> <text>processing speed</text>"
                      components={{
                        bold: (
                          <TypographyP2Bold className="inline">{''}</TypographyP2Bold>
                        ),
                        text: <TypographyP2 className="inline">{''}</TypographyP2>,
                      }}
                    />
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <User className="text-text-secondary" width={18} height={18} />
                  <TypographyP2Bold>
                    {t('endTrialModal.freePlan.features.noTeamFeatures')}
                  </TypographyP2Bold>
                </li>
              </ul>

              <div className="mt-auto">
                <Button
                  size="lg"
                  disableAutomaticSvgSizing
                  variant="outline"
                  className="w-full"
                  onClick={handleDowngrade}
                  disabled={isRevertTrialPending}
                >
                  <TypographyP>
                    {isRevertTrialPending
                      ? t('endTrialModal.freePlan.button.loading')
                      : t('endTrialModal.freePlan.button.default')}
                  </TypographyP>
                </Button>
              </div>
            </div>
          </section>
        </SegmentedControl>

        {billingInfo?.is_reverse_trial_extendable && (
          <Button
            size="lg"
            disableAutomaticSvgSizing
            variant="link"
            className="w-fit self-center"
            onClick={onExtendTrial}
          >
            {/* 14px on mobile, 16px on sm+ — matches description text above */}
            <TypographyP2 className="w-fit self-center sm:text-base">
              <Trans
                i18nKey="endTrialModal.extendTrial.prompt"
                defaults="Need more time to decide? Extend your trial <icon/>"
                components={{
                  icon: (
                    <ArrowRight
                      className="inline rtl:-scale-x-100"
                      width={18}
                      height={18}
                    />
                  ),
                }}
              />
            </TypographyP2>
          </Button>
        )}
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
